import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { User, LogOut, BarChart3, MessageSquare } from "lucide-react";
import { useAuth } from '../../core/contexts/AuthContext';
import AuthModal from '../auth/AuthModal';
import StatsModal from '../modals/StatsModal';
import StreakBadge from '../badges/StreakBadge';

export function Header() {
  const { currentUser, userProfile, logout } = useAuth();
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authMode, setAuthMode] = useState('signin');
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [showStatsModal, setShowStatsModal] = useState(false);
  const menuRef = useRef(null);

  const handleProfileClick = () => {
    if (!currentUser) {
      setAuthMode('signin');
      setShowAuthModal(true);
    } else {
      setShowProfileMenu(!showProfileMenu);
    }
  };

  const handleSignOut = async () => {
    try {
      await logout();
      setShowProfileMenu(false);
    } catch (error) {
      console.error('Failed to sign out:', error);
    }
  };

  const handleViewStats = () => {
    setShowStatsModal(true);
    setShowProfileMenu(false);
  };

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowProfileMenu(false);
      }
    };

    if (showProfileMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showProfileMenu]);

  return (
    <>
      <header
        className="sticky top-0 z-50 px-6 py-4 backdrop-blur-md"
        style={{
          backgroundColor: 'rgba(10, 10, 10, 0.85)',
          borderBottom: '1px solid #3A4238'
        }}
      >
        <div className="max-w-[1440px] mx-auto flex items-center justify-between">
          {/* Left - Streak Badge */}
          <div className="flex items-center">
            {currentUser && userProfile ? (
              <StreakBadge
                streak={userProfile?.stats?.currentStreak || 0}
                onClick={handleViewStats}
              />
            ) : (
              <div className="w-10"></div>
            )}
          </div>

          {/* Center - HU GAMES title */}
          <Link
            to="/"
            className="uppercase tracking-[0.2em] cursor-pointer transition-opacity duration-200 hover:opacity-80"
            style={{
              color: '#8B9D83',
              fontSize: '28px',
              fontWeight: '500',
              letterSpacing: '0.2em',
              textDecoration: 'none'
            }}
          >
            HU GAMES
          </Link>

          {/* Right - Profile icon with dropdown */}
          <div className="flex items-center gap-3">
            <div className="relative" ref={menuRef}>
              <button
                onClick={handleProfileClick}
                className="w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 hover:bg-opacity-100"
                style={{
                  backgroundColor: '#1A1A1A',
                  border: '1px solid #3A4238'
                }}
                aria-label="Profile"
              >
                <User size={20} style={{ color: '#8B9D83' }} />
              </button>

            {/* Dropdown Menu for authenticated users */}
            {showProfileMenu && currentUser && (
              <div
                className="absolute right-0 mt-2 w-64 rounded-lg shadow-lg overflow-hidden"
                style={{
                  backgroundColor: '#1A1A1A',
                  border: '1px solid #3A4238',
                  zIndex: 1000
                }}
              >
                {/* User Info */}
                <div
                  className="px-4 py-3"
                  style={{
                    borderBottom: '1px solid #3A4238'
                  }}
                >
                  <p
                    className="text-sm font-medium"
                    style={{ color: '#8B9D83' }}
                  >
                    {userProfile?.username || 'User'}
                  </p>
                </div>

                {/* View Stats Button */}
                <button
                  onClick={handleViewStats}
                  className="w-full px-4 py-3 text-left flex items-center gap-3 transition-all duration-200"
                  style={{
                    color: '#E8DCC4',
                    backgroundColor: 'transparent'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#2A2A2A'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                >
                  <BarChart3 size={18} style={{ color: '#8B9D83' }} />
                  <span>View Stats</span>
                </button>

                {/* Give Feedback Button (Canny) */}
                <a
                  href="https://hugames.canny.io"
                  data-canny-link
                  className="w-full px-4 py-3 text-left flex items-center gap-3 transition-all duration-200 no-underline"
                  style={{
                    color: '#E8DCC4',
                    backgroundColor: 'transparent',
                    textDecoration: 'none'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#2A2A2A'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                  onClick={() => setShowProfileMenu(false)}
                >
                  <MessageSquare size={18} style={{ color: '#8B9D83' }} />
                  <span>Give Feedback</span>
                </a>

                {/* Sign Out Button */}
                <button
                  onClick={handleSignOut}
                  className="w-full px-4 py-3 text-left flex items-center gap-3 transition-all duration-200"
                  style={{
                    color: '#E8DCC4',
                    backgroundColor: 'transparent'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#2A2A2A'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                >
                  <LogOut size={18} style={{ color: '#8B9D83' }} />
                  <span>Sign Out</span>
                </button>
              </div>
            )}
            </div>
          </div>
        </div>
      </header>

      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        initialMode={authMode}
      />

      <StatsModal
        isOpen={showStatsModal}
        onClose={() => setShowStatsModal(false)}
        userProfile={userProfile}
      />
    </>
  );
}
